import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, ShoppingBag } from 'lucide-react';
import { fetchProducts } from '../api/client';
import { useLocale } from '../context/LocaleContext';
import { useStore } from '../context/StoreContext';
import { sortSizes } from '../lib/sizes';
import { absMediaUrl, productImageFront, productImageBack } from '../components/ProductCard';
import { cdnImage, cdnSrc } from '../utils/cdnImage';

// A small clothes-hanger hook drawn in SVG, sits over the rail bar.
const Hook = ({ active }) => (
  <svg width="26" height="30" viewBox="0 0 26 30" fill="none" className="mx-auto" aria-hidden="true">
    <path
      d="M13 3c3 0 4.5 2 4.5 4.2 0 1.8-1.2 2.9-2.6 3.6"
      stroke={active ? '#22c55e' : '#9ca3af'}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
    {/* hanger shoulders */}
    <path
      d="M13 11 L3 24 Q2.5 25.5 4 25.5 L22 25.5 Q23.5 25.5 23 24 Z"
      stroke={active ? '#22c55e' : '#6b7280'}
      strokeWidth="1.8"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const RailItem = ({ product, active, onSelect, index }) => {
  const front = cdnImage(absMediaUrl(productImageFront(product)), 260, {
    widths: [160, 240, 320],
    sizes: '(max-width: 768px) 32vw, 120px',
  });
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`rail-item-${product.id}`}
      aria-pressed={active}
      className="group/hanger relative shrink-0 w-[96px] sm:w-[112px] md:w-[128px] flex flex-col items-center snap-center outline-none"
      style={{ animation: `railFade 0.5s ease ${index * 60}ms both` }}
    >
      {/* Hook hangs from the rail line above */}
      <div className={`relative z-10 -mb-1 transition-transform duration-300 ${active ? '-translate-y-1' : ''}`}>
        <Hook active={active} />
      </div>
      {/* Garment hangs from the hook and sways gently, like on a real rail */}
      <div
        className="origin-top will-change-transform transition-transform duration-300"
        style={active ? { transform: 'translateY(-6px)' } : { animation: `railSway ${2.6 + (index % 4) * 0.3}s ease-in-out ${(index % 5) * 0.25}s infinite` }}
      >
        <div
          className={`relative aspect-[3/4] w-full overflow-hidden rounded-md bg-neutral-100 ring-2 transition-all duration-300
            ${active ? 'ring-[#22c55e] shadow-[0_10px_26px_-10px_rgba(34,197,94,0.6)]' : 'ring-transparent group-hover/hanger:ring-neutral-300'}`}
        >
          <img
            src={front.src}
            srcSet={front.srcSet}
            sizes={front.sizes}
            alt={product.name}
            width={260}
            height={346}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
          {!active && <div className="absolute inset-0 bg-black/0 group-hover/hanger:bg-black/5 transition" />}
        </div>
      </div>
      <span className={`mt-2 text-[10px] font-semibold tracking-wide line-clamp-1 max-w-full px-1 transition-colors ${active ? 'text-[#16a34a]' : 'text-neutral-500'}`}>
        {product.name}
      </span>
    </button>
  );
};

const ShopTheRail = () => {
  const { productPrice, productDiscount, currency, t } = useLocale();
  const { addToCart } = useStore();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(0);
  const [side, setSide] = useState('front');
  const [size, setSize] = useState(null);
  const [sizeErr, setSizeErr] = useState(false);
  const [adding, setAdding] = useState(false);
  const railRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchProducts({ sort: 'new', limit: 10 });
        setProducts(res.items || []);
      } catch (err) {
        console.warn('[ShopTheRail] fetch failed:', err.message);
      }
    })();
  }, []);

  useEffect(() => {
    setSide('front'); // reset to front whenever a new garment is picked
    setSize(null);
    setSizeErr(false);
  }, [selected]);

  if (!products.length) return null;

  const active = products[selected] || products[0];
  const frontUrl = absMediaUrl(productImageFront(active));
  const backUrl = absMediaUrl(productImageBack(active));
  const hasBack = !!backUrl;
  const stageUrl = side === 'back' && hasBack ? backUrl : frontUrl;
  const disc = productDiscount(active);
  const sizes = sortSizes(Array.isArray(active.sizes) ? active.sizes : []);

  const handleAdd = async () => {
    if (!size) { setSizeErr(true); return; }
    setSizeErr(false);
    setAdding(true);
    try {
      await addToCart(active, size);
    } finally {
      setAdding(false);
    }
  };

  const scrollRail = (dir) => {
    const el = railRef.current;
    if (el) el.scrollBy({ left: dir * 360, behavior: 'smooth' });
  };

  return (
    <section className="bg-[#f7f7f5] py-10 md:py-16 overflow-hidden" data-testid="shop-the-rail">
      <style>{`
        @keyframes railFade { from { opacity:0; transform:translateY(12px);} to {opacity:1; transform:translateY(0);} }
        @keyframes railSway { 0%,100%{transform:rotate(-2deg);} 50%{transform:rotate(2deg);} }
        @keyframes stageFade { from {opacity:0; transform:scale(0.97);} to {opacity:1; transform:scale(1);} }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="shop-the-rail"] * { animation: none !important; }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="mb-6 md:mb-10">
          <span className="inline-block text-[10px] md:text-[11px] font-black tracking-[0.3em] text-[#16a34a]">● THE WARDROBE</span>
          <h2 className="text-2xl md:text-4xl font-black tracking-tight mt-1.5 text-neutral-900">
            SHOP THE <span className="text-[#22c55e]">RAIL</span>
          </h2>
          <p className="text-neutral-500 mt-1.5 text-xs md:text-sm">Browse the drop like a wardrobe — pull a piece off the rail to see it up close.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 items-center">
          {/* Stage */}
          <div className="lg:col-span-2">
            <div className="relative mx-auto max-w-[240px] sm:max-w-[260px]">
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
                <img
                  key={stageUrl}
                  src={cdnSrc(stageUrl, 600)}
                  alt={`${active.name} ${side} view`}
                  className="h-full w-full object-cover"
                  style={{ animation: 'stageFade 0.35s ease' }}
                  decoding="async"
                  data-testid="rail-stage-image"
                />
                {disc > 0 && (
                  <div className="absolute top-3 left-3 bg-[#22c55e] text-black text-[11px] font-black px-2.5 py-1 rounded">
                    {disc}% OFF
                  </div>
                )}
                {/* Front / Back flip */}
                {hasBack && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 bg-black/70 backdrop-blur rounded-full p-1">
                    {['front', 'back'].map((s) => (
                      <button
                        key={s}
                        onClick={() => setSide(s)}
                        data-testid={`rail-${s}-btn`}
                        className={`px-4 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
                          side === s ? 'bg-[#22c55e] text-black' : 'text-white hover:text-[#22c55e]'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-wide text-neutral-400">{active.collection}</div>
                <h3 className="text-lg md:text-xl font-black text-neutral-900 mt-0.5 line-clamp-2">{active.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xl font-black text-neutral-900">{productPrice(active, 'price')}</span>
                  {disc > 0 && (
                    <>
                      <span className="text-sm text-neutral-400 line-through">{productPrice(active, 'mrp')}</span>
                      <span className="text-sm text-[#22c55e] font-bold">{disc}% off</span>
                    </>
                  )}
                  {currency === 'EUR' && <span className="text-[10px] text-neutral-400">incl. VAT</span>}
                </div>

                {/* Size picker */}
                {sizes.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">{t('selectSize')}</span>
                      {sizeErr && <span className="text-[11px] text-[#22c55e] font-semibold" data-testid="rail-size-error">{t('pleaseSelectSize')}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {sizes.map((s) => (
                        <button
                          key={s}
                          onClick={() => { setSize(s); setSizeErr(false); }}
                          data-testid={`rail-size-${s}`}
                          className={`min-w-9 h-9 px-3 rounded-full border-2 text-xs font-bold transition ${
                            size === s ? 'border-[#22c55e] bg-[#22c55e] text-white' : 'border-neutral-300 text-neutral-700 hover:border-black'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <button
                    onClick={handleAdd}
                    disabled={adding}
                    data-testid="rail-add-to-bag"
                    className="group/add inline-flex items-center gap-2 bg-[#22c55e] text-black font-black text-sm tracking-wider px-6 py-3 rounded-full hover:bg-[#16a34a] hover:text-white transition-colors disabled:opacity-60"
                  >
                    <ShoppingBag size={16} />
                    {adding ? '...' : t('addToBag')}
                  </button>
                  <Link
                    to={`/product/${active.id}`}
                    data-testid="rail-cta"
                    className="group/cta inline-flex items-center gap-2 text-neutral-900 font-black text-sm tracking-wider px-2 py-3 hover:text-[#22c55e] transition-colors"
                  >
                    VIEW PRODUCT
                    <ArrowRight size={16} className="group-hover/cta:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* The rail */}
          <div className="lg:col-span-3 relative">
            {/* Rail bar */}
            <div className="relative">
              <div className="absolute left-0 right-0 top-[14px] h-[6px] rounded-full bg-gradient-to-b from-neutral-300 to-neutral-400 shadow-inner z-0" />
              {/* rail end caps */}
              <div className="absolute left-0 top-[9px] w-3 h-4 rounded bg-neutral-500 z-0" />
              <div className="absolute right-0 top-[9px] w-3 h-4 rounded bg-neutral-500 z-0" />

              <div
                ref={railRef}
                className="relative z-[1] flex gap-4 md:gap-6 overflow-x-auto pb-4 pt-0 snap-x scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none]"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <style>{`[data-testid="shop-the-rail"] ::-webkit-scrollbar{display:none}`}</style>
                {products.map((p, i) => (
                  <RailItem
                    key={p.id}
                    product={p}
                    index={i}
                    active={i === selected}
                    onSelect={() => setSelected(i)}
                  />
                ))}
              </div>
            </div>

            {/* Arrows */}
            <div className="hidden md:flex justify-end gap-2 mt-2">
              <button
                onClick={() => scrollRail(-1)}
                data-testid="rail-scroll-left"
                className="w-10 h-10 rounded-full border border-neutral-300 bg-white flex items-center justify-center hover:border-[#22c55e] hover:text-[#22c55e] transition"
                aria-label="Scroll rail left"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => scrollRail(1)}
                data-testid="rail-scroll-right"
                className="w-10 h-10 rounded-full border border-neutral-300 bg-white flex items-center justify-center hover:border-[#22c55e] hover:text-[#22c55e] transition"
                aria-label="Scroll rail right"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShopTheRail;
